import { useQuill } from 'react-quilljs';
import { useCallback, useEffect, useState } from 'react';
import { Box, Typography } from '@mui/material';
import { CollaboratorsList } from '../../CollaboratorsList';
import { useNotesContext } from '../../../hooks/useNotesContext';

const EditorComponent = ({
  description,
  setDescription,
  anchorEl,
  setAnchorEl,
  focused,
  setFocused,
  recording,
  stopRecording,
  setDescriptionLength,
  handleFileChange,
  isNewThread,
}) => {
  const { collaborators } = useNotesContext();

  const [isSettingDescription, setIsSettingDescription] = useState(false);
  const [time, setTime] = useState(0);
  const [intervalId, setIntervalId] = useState(null);
  const { quill, quillRef, Quill } = useQuill({
    modules: { toolbar: false },
    placeholder: 'Write a text...',
  });

  useEffect(() => {
    if (Quill) {
      const Link = Quill.import('formats/link');
      Link.sanitize = function (url) {
        let protocol = url.slice(0, url.indexOf(':'));
        if (this.PROTOCOL_WHITELIST.indexOf(protocol) === -1) {
          url = `http://${url}`;
        }
        const anchor = document.createElement('a');
        anchor.href = url;
        protocol = anchor.href.slice(0, anchor.href.indexOf(':'));
        return this.PROTOCOL_WHITELIST.indexOf(protocol) > -1 ? url : this.SANITIZED_URL;
      };
      Quill.register(Link, true);
    }
  }, [Quill]);

  useEffect(() => {
    if (recording) {
      const id = setInterval(() => {
        setTime((prevTime) => prevTime + 1);
      }, 1000);
      setIntervalId(id);
    } else if (!recording && intervalId) {
      clearInterval(intervalId);
      setIntervalId(null);
    }

    return () => clearInterval(intervalId);
  }, [recording]);

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs < 10 ? '0' : ''}${secs}`;
  };

  const containsAt = /(^|\s)@(\s|$)/;

  const handleOpenCollaboratorList = (position) => {
    setAnchorEl(position);
  };

  function getTextFromHTML(html) {
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = html;
    return tempDiv.textContent || tempDiv.innerText || '';
  }

  useEffect(() => {
    if (quill) {
      const handleTextChange = () => {
        if (!isSettingDescription) {
          const currentContent = quill.getSemanticHTML();
          const currentText = getTextFromHTML(currentContent);
          if (containsAt.test(currentText)) {
            if (containsAt.test(currentText)) {
              handleOpenCollaboratorList(quill.container);
            }
          }
          if (currentContent !== description) {
            setDescription(currentContent);
          }

          const textContent = quill.getText().replace(/\n/g, '');
          if (setDescriptionLength) {
            setDescriptionLength(textContent.length);
          }
        }
      };

      quill.on('text-change', handleTextChange);

      quill.on('selection-change', () => {
        // Don't automatically set focus when selection changes
        // This prevents autofocusing behavior
        // The user can still focus by clicking on the field
      });

      const handlePaste = async (event) => {
        const clipboardItems = event.clipboardData?.items as DataTransferItemList;
        const items = Array.from(clipboardItems).filter((item) => item.type.includes('image'));

        if (items.length) {
          event.preventDefault();
          const file = items[0].getAsFile();
          if (file) {
            const dataTransfer = new DataTransfer();
            dataTransfer.items.add(file);

            const fileInputEvent = new Event('change', { bubbles: true });
            Object.defineProperty(fileInputEvent, 'target', {
              writable: false,
              value: { files: dataTransfer.files },
            });

            handleFileChange(fileInputEvent);

            setTimeout(() => {
              const imgElements = quill.root.querySelectorAll('img');
              imgElements.forEach((img) => img.remove());
            }, 100);
          }
        }
      };

      quill.root.addEventListener('paste', handlePaste);

      return () => {
        quill.off('text-change', handleTextChange);
        quill.off('selection-change');
        quill.root.removeEventListener('paste', handlePaste);
      };
    }
  }, [
    quill,
    description,
    setDescription,
    setFocused,
    setDescriptionLength,
    handleFileChange,
    isSettingDescription,
  ]);

  const applyStyles = useCallback(() => {
    const editorElement = document.querySelector('.ql-editor') as HTMLElement;
    if (editorElement) {
      editorElement.style.transition = '0.3s ease-in-out';
      editorElement.style.minHeight = focused ? '90px' : '0';
      // editorElement.style.padding = '0'
    }
  }, [focused]);

  useEffect(() => {
    applyStyles();
  }, [applyStyles]);

  useEffect(() => {
    if (quill) {
      const handleDescriptionUpdate = () => {
        setIsSettingDescription(true);
        quill.clipboard.dangerouslyPasteHTML(description);
        const imgElements = quill.root.querySelectorAll('img');
        imgElements.forEach((img) => img.remove());
        quill.setSelection(null);
        setIsSettingDescription(false);
      };

      if (!isSettingDescription && quill.getSemanticHTML() !== description) {
        handleDescriptionUpdate();
      }

      return () => {
        quill.off('editor-change', handleDescriptionUpdate);
      };
    }
  }, [description, quill, isSettingDescription]);

  return (
    <>
      <div
        style={{
          width: '100%',
          minHeight: isNewThread ? '170px' : '0',
          height: 'auto',
          border: 'none',
          opacity: recording ? 0.5 : 1,
          pointerEvents: recording ? 'none' : 'auto',
        }}
      >
        <div ref={quillRef} style={{ border: 'none' }} />
        <CollaboratorsList
          description={description}
          collaborators={collaborators}
          anchorEl={anchorEl}
          setAnchorEl={setAnchorEl}
          quill={quill}
        />
      </div>
      {recording && (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            border: '1px solid #EAECF0',
            borderRadius: '6px',
            background: '#F9FAFB',
            padding: '2px',
            height: '70px',
            maxHeight: '70px',
            width: '100%',
          }}
        >
          <Box
            onClick={() => {
              setFocused(true);
              stopRecording();
            }}
            sx={{
              display: 'flex',
              alignItems: 'center',
              color: 'red',
              gap: '10px',
              cursor: 'pointer',
            }}
          >
            <i className="fa-solid fa-circle-stop" style={{ color: 'inherit', fontSize: '33px' }} />
            <Typography sx={{ color: '#000000' }}>Recording...</Typography>
          </Box>
          <Box
            sx={{
              width: '50px',
              height: '30px',
              borderRadius: '16px',
              background: '#F2F4F7',
            }}
          >
            <Typography>{formatTime(time)}</Typography>
          </Box>
        </Box>
      )}
    </>
  );
};

export default EditorComponent;
